import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
  FirestoreDataConverter,
} from 'firebase/firestore';
import type { Output } from '@segmently/schema-models/entities';
import { CoreFirebaseService } from './CoreFirebaseService';
import { outputConverter as clientOutputConverter } from '../converters/outputConverter';

/**
 * OutputService
 *
 * Type-safe service for Output entity CRUD operations.
 * Outputs are stored as subcollection under studycases.
 *
 * Path: projects/{projectId}/studycases/{studyCaseId}/outputs/{outputId}
 *
 * Features:
 * - Basic CRUD operations (create, read, delete)
 * - Type-safe: only Output types exposed, no Firebase types in public API
 * - Scoped to parent StudyCase
 * - Uses outputConverter for data conversion
 *
 * Usage:
 * ```typescript
 * import { OutputService } from '@/modules/firebase-core/services/OutputService';
 *
 * const service = new OutputService('project-id', 'studycase-id');
 *
 * // Create
 * const output = await service.create({});
 *
 * // Read
 * const found = await service.get(output.id!);
 *
 * // Delete
 * await service.delete(output.id!);
 * ```
 */
export class OutputService {
  private readonly collectionName = 'outputs';
  private readonly firestore = CoreFirebaseService.getInstance().firestore;
  private readonly converter: FirestoreDataConverter<Output>;
  private readonly projectId: string;
  private readonly studyCaseId: string;

  /**
   * Create a new OutputService instance
   *
   * @param projectId - Parent project ID
   * @param studyCaseId - Parent study case ID
   * @param converter - Firestore converter for Output entity (defaults to client SDK converter)
   */
  constructor(
    projectId: string,
    studyCaseId: string,
    converter: FirestoreDataConverter<Output> = clientOutputConverter
  ) {
    this.projectId = projectId;
    this.studyCaseId = studyCaseId;
    this.converter = converter;
  }

  /**
   * Create a new output
   *
   * @param data - Partial output data (id will be auto-generated if not provided)
   * @returns {Promise<Output>} The created output with generated ID
   *
   * @example
   * ```typescript
   * const output = await service.create({});
   * console.log(output.id); // Auto-generated ID
   * ```
   */
  async create(data: Partial<Output>): Promise<Output> {
    try {
      const collectionRef = collection(
        this.firestore,
        'projects',
        this.projectId,
        'studycases',
        this.studyCaseId,
        this.collectionName
      );
      const docRef = doc(collectionRef).withConverter(this.converter);

      const outputData: Output = {
        ...data,
        id: docRef.id,
      } as Output;

      await setDoc(docRef, outputData);

      return outputData;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'OutputService.create');
      throw error;
    }
  }

  /**
   * Get an output by ID
   *
   * @param id - Output ID
   * @returns {Promise<Output | null>} The output or null if not found
   *
   * @example
   * ```typescript
   * const output = await service.get('output-123');
   * if (output) {
   *   console.log(output.id);
   * }
   * ```
   */
  async get(id: string): Promise<Output | null> {
    try {
      const docRef = doc(
        this.firestore,
        'projects',
        this.projectId,
        'studycases',
        this.studyCaseId,
        this.collectionName,
        id
      ).withConverter(this.converter);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      return { ...data, id: docSnap.id } as Output;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'OutputService.get');
      throw error;
    }
  }

  /**
   * Delete an output
   *
   * @param id - Output ID
   * @returns {Promise<void>}
   *
   * @example
   * ```typescript
   * await service.delete('output-123');
   * ```
   */
  async delete(id: string): Promise<void> {
    try {
      const docRef = doc(
        this.firestore,
        'projects',
        this.projectId,
        'studycases',
        this.studyCaseId,
        this.collectionName,
        id
      );
      await deleteDoc(docRef);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'OutputService.delete');
      throw error;
    }
  }

  /**
   * List outputs for the study case
   *
   * @param options - Query options
   * @param options.limit - Maximum number of outputs to return (default: 1000)
   * @returns {Promise<Output[]>} Array of outputs
   *
   * @example
   * ```typescript
   * const outputs = await service.list({ limit: 100 });
   * ```
   */
  async list(options: { limit?: number } = {}): Promise<Output[]> {
    try {
      const collectionRef = collection(
        this.firestore,
        'projects',
        this.projectId,
        'studycases',
        this.studyCaseId,
        this.collectionName
      ).withConverter(this.converter);

      const pageLimit = options.limit || 1000;
      const q = query(collectionRef, limit(pageLimit));

      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      } as Output));
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'OutputService.list');
      throw error;
    }
  }
}
